import { useTranslation } from '@numistic/i18n'
import { Button } from '@numistic/ui'
import { useNavigate, useRouterState } from '@tanstack/react-router'
import { useEffect, useRef } from 'react'

/** Сочетания клавиш (docs/PLAN.md, этап 3); справка — по «?». */
export const SHORTCUTS = [
  ['/', 'search'],
  ['g c', 'catalog'],
  ['g k', 'collections'],
  ['g s', 'stats'],
  ['n', 'new'],
  ['Ctrl+Enter', 'submit'],
  ['?', 'help'],
  ['Esc', 'close'],
] as const

const typing = (el: EventTarget | null) =>
  el instanceof HTMLElement &&
  (el.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName))

/**
 * Глобальные клавиши. В полях ввода работает только Ctrl/⌘+Enter — отправка формы,
 * остальные клавиши не мешают набору текста.
 */
export function useHotkeys(onHelp: () => void) {
  const navigate = useNavigate()
  const path = useRouterState({ select: (s) => s.location.pathname })
  const search = useRouterState({ select: (s) => s.location.search as Record<string, unknown> })
  const pendingG = useRef<number | null>(null)

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.defaultPrevented) return
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        const form = (e.target as HTMLElement | null)?.closest?.('form')
        if (form) {
          e.preventDefault()
          form.requestSubmit()
        }
        return
      }
      if (e.ctrlKey || e.metaKey || e.altKey || typing(e.target)) return
      // Открыто модальное окно (справка по клавишам и т.п.) — навигация под ним не нужна.
      if (document.querySelector('dialog[open], [aria-modal="true"]')) return

      if (pendingG.current !== null) {
        window.clearTimeout(pendingG.current)
        pendingG.current = null
        const to = { c: '/catalog', k: '/c', s: '/stats' }[e.key]
        if (to) {
          e.preventDefault()
          void navigate({ to, search: {} })
        }
        return
      }
      if (e.key === 'g') {
        pendingG.current = window.setTimeout(() => (pendingG.current = null), 1500)
        return
      }
      if (e.key === '/') {
        const field = document.querySelector<HTMLInputElement>('main input[type=search]')
        if (field) {
          e.preventDefault()
          field.focus()
        }
        return
      }
      if (e.key === '?') {
        e.preventDefault()
        onHelp()
        return
      }
      if (e.key === 'n') {
        if (path === '/catalog') void navigate({ to: '/catalog/new', search: {} })
        else if (path === '/c')
          void navigate({
            to: '/s/new',
            search: {
              collection: typeof search.collection === 'string' ? search.collection : undefined,
            },
          })
      }
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [navigate, onHelp, path, search])
}

/** Справка по клавишам: модальное окно, Esc закрывает, фокус возвращается. */
export function HotkeysHelp({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation()
  const closeRef = useRef<HTMLButtonElement>(null)
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null
    closeRef.current?.focus()
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('keydown', onKey)
      previous?.focus()
    }
  }, [onClose])
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="hotkeys-title"
        className="grid w-full max-w-md gap-4 rounded-lg border bg-card p-6 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="hotkeys-title" className="text-lg font-semibold">
          {t('hotkeys.title')}
        </h2>
        <dl className="grid grid-cols-[7rem_1fr] gap-x-4 gap-y-2 text-sm">
          {SHORTCUTS.map(([keys, action]) => (
            <div key={action} className="contents">
              <dt>
                {keys.split(' ').map((k, i) => (
                  <kbd
                    key={i}
                    className="mr-1 rounded border bg-muted px-1.5 py-0.5 font-mono text-xs"
                  >
                    {k}
                  </kbd>
                ))}
              </dt>
              <dd className="text-muted-foreground">{t(`hotkeys.actions.${action}`)}</dd>
            </div>
          ))}
        </dl>
        <Button ref={closeRef} variant="outline" className="justify-self-end" onClick={onClose}>
          {t('common.close')}
        </Button>
      </div>
    </div>
  )
}
